import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {Promises, schedule} from "../../common/utils/promises";
import {resolve} from "../../container";
import {Initializing, UnLitElement} from "../../common/elements";
import type {EopMacherbusRankedProject} from "./macherbusRankedProject";
import type {Ranking} from "./macherbusService";
import {MacherbusService} from "./macherbusService";
import {LocatedDates} from "../../common/utils/dates/locatedDates";
import {Dictionary} from "../../page/elements/dictionary";
import {elementFrom} from "../../common/utils/html";

@customElement("eop-macherbus-ranking")
export class EopMacherbusRanking extends Initializing(UnLitElement) {

    @property({attribute: "voting-id"})
    private votingId: string;
    @property({attribute: "language"})
    private language: string;
    @state()
    private snapshotTime: Date = new Date();
    @state()
    private projects: EopMacherbusRankedProject[] = [];

    private dictionary: Dictionary;

    public constructor(
        private promises: Promises = resolve(Promises),
        private macherbusService: MacherbusService = resolve(MacherbusService),
        private dates: LocatedDates = resolve(LocatedDates)
    ) {
        super();
    }

    public render(): TemplateResult {
        return html`
            <div class="macherbus-module">
                <eop-overlay-spinner event="ranked" class="ranking-spinner"></eop-overlay-spinner>
                <div class="head">
                    <div class="snapshot-time">${this.dictionary.translate("SNAPSHOT_TIME_LABEL")} ${this.dates.toLocalDateString(this.snapshotTime)}, ${this.dates.toLocalTimeString(this.snapshotTime)}</div>
                </div>
                <div class="macherbus-projects">${this.projects}</div>
            </div>
        `;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        void this.init();
    }

    private async init(): Promise<void> {
        this.projects = Array.from(this.querySelectorAll<HTMLTemplateElement>(".macherbus-project-template"))
            .map(template => elementFrom<EopMacherbusRankedProject>(template.innerHTML));
        const rankProjectsPromise = this.rankProjects(this.votingId);
        const spinnerForRanking = this.promises.decoratorFor("ranked")(rankProjectsPromise);
        await schedule(spinnerForRanking).as("ranking");
        this.initialized();
    }

    public async rankProjects(votingId: string): Promise<void> {
        const ranking = await this.macherbusService.getRanking(
            votingId,
            this.language,
            this.projects.map(p => p.projectId));
        this.snapshotTime = new Date(ranking.snapshotTime);
        this.updateRanking(ranking);
        this.sortProjects();
    }

    private updateRanking(ranking: Ranking): void {
        ranking.rankedProjects.forEach(project => {
            this.projects
                .findFirst(p => p.projectId === project.projectId)
                ?.setRanking(project.rank, project.voteCount);
        });
    }

    private sortProjects(): void {
        this.projects = Array.from(this.projects).sort((a, b) => a.compareTo(b));
    }
}